import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import { Info } from 'lucide-react';
import { GRAY_400 } from 'theme/grayScale';

export interface InfoTooltipProps {
  text: React.ReactNode;
  size?: number;
  /** Overrides the default "More information" label for a more specific one. */
  ariaLabel?: string;
  /**
   * Overrides the tooltip popper's z-index. Defaults to 10001 — one above
   * the modal overlay's own z-index of 10000 (see AsyncSearchSelect.tsx's
   * `menuPortal` comment and ManufacturerPrecedenceOrderTooltip.tsx's own
   * hardcoded 10001) — MUI's own default (1500) renders behind any modal,
   * which is exactly why every Details-tab InfoTooltip was invisible before
   * this default existed (issue #653, Task 16 QA follow-up). Raise further
   * (e.g. 10003, matching ExternalLinkSuggestionsPane.tsx) only for a
   * tooltip stacked under a SECOND modal on top of the first.
   */
  zIndex?: number;
}

const DEFAULT_TOOLTIP_Z_INDEX = 10001;

/**
 * Accessible info-icon tooltip trigger (issue #653, Task 13, Sub-task
 * 13.2 — Decision 12: "every status-like value shown to a user carries a
 * tooltip... [must be] keyboard accessible"). Renders a real `<button>` so
 * it's naturally focusable/tabbable — MUI's `Tooltip` shows on focus as
 * well as hover for an interactive child, but the bare `<span
 * style={{cursor:'help'}}>` this replaced (StatusSection.tsx's local
 * `InfoTooltip`) has no `tabIndex` and is invisible to keyboard-only
 * navigation entirely. Mirrors the button-based pattern already proven in
 * `PrecedenceOrderTooltip.tsx`/`ManufacturerPrecedenceOrderTooltip.tsx`
 * (both already accessible; not touched here since they already work).
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  size = 13,
  ariaLabel = 'More information',
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
}) => (
  <MuiTooltip
    title={text}
    placement="right"
    arrow
    slotProps={{ popper: { style: { zIndex } } }}>
    <button
      type="button"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        color: GRAY_400,
        cursor: 'help',
        background: 'none',
        border: 'none',
        padding: 0,
        margin: '0 0 0 4px',
      }}>
      <Info size={size} />
    </button>
  </MuiTooltip>
);

export default InfoTooltip;
